import React, { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useLatest, useLocalStorage, useMount } from 'react-use';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { createContext, useContext } from 'use-context-selector';
import { v4 as uuidv4 } from 'uuid';

import {
  API_ENDPOINT_KEYS,
  DEFAULT_COUNTRY_DATA,
  DEVICE_ID_PREFIX,
  FEATURE_FLAGS,
  PAYMENT_METHOD,
  SERVICE_AUTH_ENDPOINTS,
  SERVICE_AUTH_V2_ENDPOINTS,
  STEP,
} from '~common';
import { OngoingRentalGuard } from '~components/templates/OngoingRentalGuard';
import { AUTH_API_CLIENT_ID, HOMEPAGE_PATH, RENTAL_PAGE_PATH } from '~configs';
import { useNavigateInApp } from '~hooks';
import { useQueryGetRentalState } from '~hooks/useQueryGetRentalState';
import {
  LoggedInUserInfoProvider,
  StationInformationProvider,
  useEventTrackingContext,
  UserPaymentProvider,
  UserSubscriptionProvider,
} from '~providers';
import { postAuthRequest } from '~services';
import { inMemoryJWTService } from '~services/jwt.service';
import { RequestBatteryResponse } from '~types';
import { EjectedSubscriptionInfo } from '~types/subscription.type';
import { checkEnabledFeatureFlag, isValidPhoneNumber } from '~utils';

type MainContextType = {
  step: STEP | null;
  changeStep: (step: STEP) => void;
  handleSubmitGetOTP: () => void;
  getOTP: (captchaToken?: string) => Promise<void>;
  login: (code: string) => Promise<void>;
  isLoadingLogin: boolean;
  isLoadingOTP: boolean;
  sendOTPFailedError: string | null;
  initialComplete: boolean;
  phoneNumber: string;
  authToken: string | undefined;
  cachedPhoneNumber: string | undefined;
  setPhoneNumber: React.Dispatch<React.SetStateAction<string>>;
  setIsEnabledEjectBtn: React.Dispatch<React.SetStateAction<boolean>>;
  setSendOTPFailedError: React.Dispatch<React.SetStateAction<string | null>>;
  paymentMethod: PAYMENT_METHOD;
  onChangePaymentMethod: (method: PAYMENT_METHOD) => () => void;
  isEnabledEjectBtn: boolean;
  handleEditPaymentMethod: () => void;
  stationSerialNumber: string | null;
  agreePolicy: boolean;
  onAgreePolicy: (event: React.ChangeEvent<HTMLInputElement>) => void;
  rentalQuery?: ReturnType<typeof useQueryGetRentalState>;
  rentalId: string;
  /** The pass behind the rental we just started. `null` for pay-per-use. */
  ejectedSubscriptionInfo: EjectedSubscriptionInfo | null;
  /**
   * Stores a rent response. Keep the id and the pass together — saving only
   * the id is what left a stale swap count on the success screen.
   */
  saveRentalResult: (response: RequestBatteryResponse) => void;
};

const MainContext = createContext<MainContextType>({
  step: null,
  changeStep: () => null,
  setIsEnabledEjectBtn: () => null,
  setSendOTPFailedError: () => null,
  handleSubmitGetOTP: () => null,
  getOTP: () => Promise.resolve(),
  login: () => Promise.resolve(),
  phoneNumber: `+${DEFAULT_COUNTRY_DATA.dialCode}`,
  cachedPhoneNumber: undefined,
  handleEditPaymentMethod: () => null,
  setPhoneNumber: () => null,
  isLoadingOTP: false,
  sendOTPFailedError: null,
  isLoadingLogin: false,
  initialComplete: false,
  paymentMethod: PAYMENT_METHOD.CREDIT_CARD,
  onChangePaymentMethod: () => () => null,
  isEnabledEjectBtn: false,
  authToken: undefined,
  stationSerialNumber: '',
  agreePolicy: false,
  onAgreePolicy: () => null,
  rentalId: '',
  ejectedSubscriptionInfo: null,
  saveRentalResult: () => null,
});

type Props = {
  children: React.ReactNode;
};

export const MainProvider: React.FC<Props> = ({ children }) => {
  const [step, setStep] = React.useState<STEP | null>(null);
  const [initialComplete, setInitialComplete] = React.useState(false);
  const [isLoadingOTP, setIsLoadingOTP] = useState(false);
  const [isLoadingLogin, setIsLoadingLogin] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(
    `+${DEFAULT_COUNTRY_DATA.dialCode}`,
  );
  const [authToken, setAuthToken, remove] = useLocalStorage<string>(
    'cf-webapp-auth-token',
  );
  const [cachedPhoneNumber, setCachedPhoneNumber, removeCachedPhoneNumber] =
    useLocalStorage<string>('cf-webapp-phone-number');
  const [deviceId, setDeviceId, _] = useLocalStorage<string>(
    'cf-webapp-device-id',
  );

  const [searchParams] = useSearchParams();

  const [paymentMethod, setPaymentMethod] = useState<PAYMENT_METHOD>(
    PAYMENT_METHOD.CREDIT_CARD,
  );

  // The error text id error
  const [sendOTPFailedError, setSendOTPFailedError] = useState<string | null>(
    null,
  );

  const [isEnabledEjectBtn, setIsEnabledEjectBtn] = useState(false);

  const [agreePolicy, setAgreePolicy] = useState(false);
  const [rentalId, setRentalId] = useState('');
  const [ejectedSubscriptionInfo, setEjectedSubscriptionInfo] =
    useState<EjectedSubscriptionInfo | null>(null);

  const navigate = useNavigateInApp();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { identifyUser } = useEventTrackingContext();

  const changeStep = (step: STEP) => setStep(step);

  const handleSubmitGetOTP = () => {
    getOTP();
  };

  const getOTP = async () => {
    if (!isValidPhoneNumber(phoneNumber)) return;
    setCachedPhoneNumber(phoneNumber);
    const isEnabledUseServiceAuthV2 = checkEnabledFeatureFlag(
      FEATURE_FLAGS.WEB_APP_CHECK_RATE_LIMIT,
    );
    const sendCodeEndpoint = isEnabledUseServiceAuthV2
      ? SERVICE_AUTH_V2_ENDPOINTS
      : SERVICE_AUTH_ENDPOINTS;
    try {
      setIsLoadingOTP(true);
      const body = {
        phone_number: phoneNumber,
        platform: 'WEB',
        language: 'en',
      };
      const request = postAuthRequest({
        path: sendCodeEndpoint[API_ENDPOINT_KEYS.SEND_CODE],
        data: {
          ...body,
          client_id: AUTH_API_CLIENT_ID,
          ...(isEnabledUseServiceAuthV2 && deviceId
            ? { device_id: deviceId }
            : {}),
        },
        includeAppCheckHeader: isEnabledUseServiceAuthV2,
      });
      await request.then(() => {
        setSendOTPFailedError(null);
      });

      setStep(STEP.INPUT_OTP);
    } catch (error) {
      const textError =
        isAxiosError(error) && error?.response?.status === 429
          ? 'text.tooManyAttempts'
          : 'text.getOtpFailed';

      setSendOTPFailedError(textError);
    } finally {
      setIsLoadingOTP(false);
    }
  };

  const login = async (code: string) => {
    try {
      setIsLoadingLogin(true);
      const body = {
        phone_number: phoneNumber,
        code: code,
      };
      const request = postAuthRequest({
        path: SERVICE_AUTH_ENDPOINTS[API_ENDPOINT_KEYS.VERIFY_CODE],
        data: {
          ...body,
          client_id: AUTH_API_CLIENT_ID,
        },
      });
      await request.then((res) => {
        const { user, accessToken, expiresIn, refreshToken } = res.data;

        inMemoryJWTService.setToken(accessToken, expiresIn);
        inMemoryJWTService.setRefreshToken(refreshToken);
        identifyUser(user.id);

        setAuthToken(accessToken);
      });
    } finally {
      setIsLoadingLogin(false);
    }
  };

  const onChangePaymentMethod = (paymentMethod: PAYMENT_METHOD) => () => {
    setPaymentMethod(paymentMethod);
  };

  const handleEditPaymentMethod = () => {
    setStep(STEP.INPUT_PAYMENT_INFO);
    /**
     * Prevent navigate first then state update later caused by setState running async
     */
    setTimeout(() => {
      navigate(RENTAL_PAGE_PATH);
    });
  };

  const onAgreePolicy = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAgreePolicy(event.target.checked);
  };

  const rentalQuery = useQueryGetRentalState(rentalId);
  const stationSerialNumber = searchParams.get('box');

  const saveRentalResult = (response: RequestBatteryResponse) => {
    setRentalId(response.rentalId ?? '');
    setEjectedSubscriptionInfo(response.userSubscriptionInfo ?? null);
  };

  const kickUserToAuthenticate = () => {
    remove();
    removeCachedPhoneNumber();
    setStep(STEP.INPUT_PHONE_NUMBER);
    if (location.pathname === HOMEPAGE_PATH) {
      return;
    }
    navigate(RENTAL_PAGE_PATH);
  };

  const postPageLoadedProcess = async () => {
    try {
      if (authToken) {
        const data = await inMemoryJWTService.getNewAccessToken();
        setAuthToken(data.accessToken);
      } else {
        kickUserToAuthenticate();
      }
    } catch {
      kickUserToAuthenticate();
    } finally {
      setInitialComplete(true);
    }
  };

  useMount(postPageLoadedProcess);

  const kickUserToAuthenticateRef = useLatest(kickUserToAuthenticate);

  useEffect(() => {
    if (!initialComplete) {
      return undefined;
    }
    return inMemoryJWTService.onLogout(() => {
      queryClient.clear();
      kickUserToAuthenticateRef.current();
    });
  }, [initialComplete, kickUserToAuthenticateRef, queryClient]);

  useEffect(() => {
    if (!deviceId) {
      const newId = `${DEVICE_ID_PREFIX}${uuidv4()}`;
      setDeviceId(newId);
    }
  }, [deviceId, setDeviceId]);

  return (
    <MainContext.Provider
      value={{
        cachedPhoneNumber,
        authToken,
        handleEditPaymentMethod,
        isEnabledEjectBtn,
        setIsEnabledEjectBtn,
        setSendOTPFailedError,
        paymentMethod,
        initialComplete,
        isLoadingLogin,
        isLoadingOTP,
        sendOTPFailedError,
        step,
        changeStep,
        handleSubmitGetOTP,
        getOTP,
        phoneNumber,
        setPhoneNumber,
        login,
        onChangePaymentMethod,
        stationSerialNumber,
        agreePolicy,
        onAgreePolicy,
        rentalQuery,
        rentalId,
        ejectedSubscriptionInfo,
        saveRentalResult,
      }}
    >
      {authToken && inMemoryJWTService.getCurrentToken() ? (
        <LoggedInUserInfoProvider>
          <UserPaymentProvider>
            <StationInformationProvider>
              <OngoingRentalGuard>
                <UserSubscriptionProvider>{children}</UserSubscriptionProvider>
              </OngoingRentalGuard>
            </StationInformationProvider>
          </UserPaymentProvider>
        </LoggedInUserInfoProvider>
      ) : (
        children
      )}
    </MainContext.Provider>
  );
};

export const useMainContext = () => useContext(MainContext);
